import { Injectable, Injector } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ColDef } from 'ag-grid-community';
import { SharedFacade } from '../../../../data/src/lib/+state/shared/shared.facade';
import { HttpClient } from '@angular/common/http';
import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { DatePipe } from '@angular/common';
import * as CryptoJS from 'crypto-js';
import { Observable, Subject, debounceTime } from 'rxjs';
import { EN_LOCALE, FeatureCodes } from '../constants';
import { Router } from '@angular/router';

const secretKey = '#25eiR4fkdWHKtd';

export const noWhitespaceValidator = (control: FormControl) => {
  const value = control.value ? String(control.value) : '';
  const isWhitespace = value.trim().length === 0;
  return !isWhitespace ? null : { whitespace: true };
};
export function greaterThanZeroValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value !== null && value <= 0) {
      return { greaterThanZero: true };
    }
    return null;
  };
}
export function wrapLabel(label: string, width: number): string[] {
  const words = label.split(' ');
  let line = '';
  const lines = [];

  for (let word of words) {
    if (line.length + word.length + 1 <= width) {
      line += (line.length ? ' ' : '') + word;
    } else {
      lines.push(line);
      line = word;
    }
  }

  if (line.length) {
    lines.push(line);
  }

  return lines;
}
export const doubleCLickEventOnFirstColumn = (params: any) => {
  const cellValue = params.value;
  const cellElement = document.createElement('span');
  cellElement.style.cursor = 'pointer';
  cellElement.innerHTML = `${cellValue}`;
  return cellElement;
};

export const convertAbbreviations = (objects: any) => {
  const convertedObjects = objects?.map((obj: any) => {
    const newName = obj?.name
      ?.replace(/\bUs\b/gi, 'US')
      .replace(/\bEu\b/gi, 'EU')
      .replace(/\bUsa\b/gi, 'USA');
    const titleCasePipe = new TitleCasePipe();
    let titleCaseName;
    if (newName !== 'USA' && newName !== 'EU' && newName !== 'USA minor outlying islands')
      titleCaseName = titleCasePipe.transform(newName);
    else if (newName == 'USA minor outlying islands') titleCaseName = 'USA Minor Outlying Islands';
    else titleCaseName = newName;
    return { ...obj, name: titleCaseName };
  });
  return convertedObjects;
};

export const nonNegativeValidator = (control: any) => {
  const value = control.value;
  if (value < 0) {
    return { nonNegative: true };
  }
  return null;
};

export const abbreviateCountryStr = (str: string) => {
  switch (str?.toLowerCase()) {
    case 'usa':
      return 'USA';
    case 'usa minor outlying islands':
    case 'USA Minor Outlying Islands':
      return 'USA Minor Outlying Islands';
    case 'eu':
      return 'EU';
    case 'n/a':
      return 'N/A';
    default:
      let titleCaseVar = new TitleCasePipe();
      return titleCaseVar.transform(str);
  }
};

export const getRolesAndPermission = (moduleName: string, parentModuleName: string) => {
  const rolesDataJson: any = localStorage.getItem('access');
  const rolesDataParse = JSON.parse(rolesDataJson);
  return rolesDataParse?.find(
    (data: any) =>
      data.moduleName?.toLowerCase() == moduleName?.toLowerCase() &&
      data.parentModuleName?.toLowerCase() == parentModuleName?.toLowerCase() &&
      rolesDataParse?.filter(
        (dataParent: any) =>
          dataParent.moduleName?.toLowerCase() == parentModuleName?.toLowerCase() &&
          dataParent.moduleId == data.parentModuleId
      )[0]
  );
};

export const debounce = <T>(source: Observable<T>, debounceTimeMs: number): Observable<T> => {
  const subject = new Subject<T>();
  source.pipe(debounceTime(debounceTimeMs)).subscribe(subject);
  return subject.asObservable();
};

export const getRolesAndPermissionForProject = (moduleName: string, parentModuleName: string) => {
  const rolesDataJson: any = localStorage.getItem('access');
  const rolesDataParse = JSON.parse(rolesDataJson);
  return rolesDataParse?.find(
    (data: any) => data.moduleName == moduleName && data.parentModuleName == parentModuleName
  );
};

export const datePipe = (data: any) => {
  const inputDate = new Date(data);
  const datePipe = new DatePipe('en-US');
  return datePipe.transform(inputDate, 'M/d/yyyy h:mm:ss a');
};

export const yAxisformateValue = (number: any) => {
  if (Number.isInteger(number)) {
    // If the number is an integer, return it as is.
    return number.toString();
  } else {
    // Determine the number of decimal places dynamically based on the input number.
    const decimalPlaces = Math.max(0, -Math.floor(Math.log10(Math.abs(number))) + 1);

    // Round the number to the calculated decimal places and convert it to a string.
    return (Math.round(number * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces)).toFixed(
      decimalPlaces
    );
  }
};

export const getShortDecimalFormat = (impactValue: string): string => {
  if (impactValue && impactValue.trim() !== '') {
    const impact = parseFloat(impactValue);

    if (!isNaN(impact)) {
      const impactInt = Math.floor(impact);
      if (impactInt >= 100) {
        return impactInt.toLocaleString('en-US');
      }
    }
  }
  return impactValue;
};

export const getRandomColorWithOpacity = (opacity?: number) => {
  const chartBackGround = [
    [151, 71, 255],
    [251, 59, 255],
    [255, 46, 52],
    [255, 155, 20],
    [20, 232, 5],
    [5, 232, 250],
    [250, 211, 5],
    [251, 171, 90],
    [250, 115, 107],
    [120, 206, 179],
    [96, 170, 193],
    [163, 135, 188],
    [208, 112, 144],
    [154, 205, 50],
    [192, 144, 144],
    [173, 216, 230],
    [240, 128, 112],
    [255, 182, 193],
    [255, 160, 122],
    [32, 178, 170],
    [135, 206, 250],
    [119, 136, 153],
    [176, 196, 222],
    [240, 160, 96],
    [147, 112, 219],
    [60, 179, 113],
    [72, 209, 204],
    [255, 222, 173],
    [128, 128, 0],
    [107, 142, 35],
    [255, 69, 0],
    [255, 165, 0],
    [218, 112, 214],
    [152, 251, 152],
    [175, 238, 238],
    [219, 112, 147],
    [255, 218, 185],
    [205, 133, 63],
    [221, 160, 221],
    [188, 143, 143],
    [250, 128, 114],
    [244, 164, 96],
  ];
  let backColor: any = [];
  chartBackGround.map((rgb: any) => {
    const rgbColor = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
    const rgbaColor = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
    backColor.push({ rgb: rgbColor, rgba: rgbaColor });
  });
  return backColor;
};

export const commonColumns = [
  { colId: 'LandFill', headerName: 'Landfill %', field: 'landfill' },
  { colId: 'Recycling', headerName: 'Recycling %', field: 'recycling' },
  { colId: 'HomeComposting', headerName: 'Home composting %', field: 'homeComposting' },
  {
    colId: 'CommercialComposting',
    headerName: 'Commercial composting %',
    field: 'commercialComposting',
  },
  { colId: 'Incineration', headerName: 'Incineration %', field: 'incineration' },
  { colId: 'WasteEnergyRecovery', headerName: 'Energy recovery %', field: 'wasteEnergyRecovery' },
  { colId: 'OpenDump', headerName: 'Open dump %', field: 'openDump' },
  { colId: 'AnerobicDigestion', headerName: 'Anaerobic digestion %', field: 'anerobicDigestion' },
  { colId: 'Litter', headerName: 'Litter %', field: 'litter' },
  { colId: 'StormWater', headerName: 'Storm water %', field: 'stormWater' },
  { colId: 'Sewer', headerName: 'Sewer %', field: 'sewer' },
  { colId: 'RecyclingOpenLoop', headerName: 'Recycling, open loop %', field: 'recyclingOpenLoop' },
  { colId: 'ChemicalRecycling', headerName: 'Chemical recycling %', field: 'chemicalRecycling' },
  { colId: 'Pyrolysis', headerName: 'Pyrolysis/Gasification %', field: 'pyrolysis' },
  { colId: 'OpenBurning', headerName: 'Open burning %', field: 'openBurning' },
  { colId: 'None', headerName: 'None %', field: 'none' },
];

export const getRandomColorWithOpacityForPieChart = (opacity?: number) => {
  const chartBackGround = [
    [151, 71, 255],
    [20, 232, 5],
    [5, 232, 250],
    [251, 59, 255],
    [255, 46, 52],
    [255, 155, 20],
    [250, 211, 5],
  ];
  let backColor: any = [];
  chartBackGround.map((rgb: any) => {
    const rgbColor = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
    const rgbaColor = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
    backColor.push({ rgb: rgbColor, rgba: rgbaColor });
  });
  return backColor;
};

export function delayedFunctionCall(func: Function, delay: number, context: any, ...args: any[]) {
  setTimeout(() => {
    func.apply(context, args);
  }, delay);
}

export function setDelayedFlag<T>(obj: { value: T }, value: T, delay: number) {
  setTimeout(() => {
    obj.value = value;
  }, delay);
}

export function indexRemapping(type: any, from?: string | undefined) {
  return type?.map((item: any, i: any) => {
    const remappedData = {
      ...item,
      index: i + 1,
    };
    if (from == 'prodLoss') remappedData.productMixKeepEditing = false;
    return remappedData;
  });
}

export function formatNumber(num: any) {
  // Check if the number is an integer
  if (Number.isInteger(num)) {
    return num;
  } else {
    // Use toFixed(2) for numbers with decimal places
    return num.toFixed(2);
  }
}

export function parseNumericValue(value: string): number {
  return parseFloat(value.replace(/,/g, ''));
}

// Encrypt a value
export function encrypt(value: string): string {
  return CryptoJS.AES.encrypt(value, secretKey).toString();
}

// Decrypt a value
export function decrypt(encryptedValue: string): string {
  const bytes = CryptoJS.AES.decrypt(encryptedValue, secretKey);
  return bytes.toString(CryptoJS.enc.Utf8);
}

// ── Backend-compatible id encryption ──────────────────────────────────────────
// Mirrors PIQET.Business.Helpers.CommonHelper.EncryptId / DecryptId on the C#
// side: AES-256-CBC, zero IV, PKCS7 padding, key = UTF-8 of the secret padded
// to 32 chars, output = URL-safe base64 (`+`→`-`, `/`→`_`, no `=` padding).
// Anything encrypted by encryptId() decrypts cleanly on the backend, and
// vice versa.
const ID_ENCRYPTION_KEY_RAW = 'g7yG87HU9Dhjkhk7Tg7ttydhgbj';
const ID_ENCRYPTION_KEY = CryptoJS.enc.Utf8.parse(
  ID_ENCRYPTION_KEY_RAW.padEnd(32, ' ').substring(0, 32)
);
const ID_ENCRYPTION_IV = CryptoJS.enc.Hex.parse('00000000000000000000000000000000');

// Encrypt an id (number/string) for use in URLs.
// Returns '' for null/undefined/empty/0 so callers can pass it directly.
export function encryptId(id: number | string | null | undefined): string {
  if (id === null || id === undefined || id === '' || id === 0) return '';
  const cipher = CryptoJS.AES.encrypt(String(id), ID_ENCRYPTION_KEY, {
    iv: ID_ENCRYPTION_IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const base64 = cipher.ciphertext.toString(CryptoJS.enc.Base64);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Decrypt an id read from a URL param. Returns 0 when input is missing or invalid.
export function decryptId(encryptedValue: string | null | undefined): number {
  if (!encryptedValue) return 0;
  try {
    let base64 = encryptedValue.replace(/-/g, '+').replace(/_/g, '/');
    const remainder = base64.length % 4;
    if (remainder === 2) base64 += '==';
    else if (remainder === 3) base64 += '=';
    else if (remainder === 1) return 0;
    const decrypted = CryptoJS.AES.decrypt(
      CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Base64.parse(base64),
      }),
      ID_ENCRYPTION_KEY,
      {
        iv: ID_ENCRYPTION_IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );
    const text = decrypted.toString(CryptoJS.enc.Utf8);
    const parsed = parseInt(text, 10);
    return isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
}

export function customEncode(inputString: string) {
  return inputString.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function customDecode(inputString: string) {
  return inputString?.replace('-', '+').replace('_', '/').padEnd(4, '=');
}

export function nameFormatter(data: any) {
  switch (data?.data.countryName) {
    case 'Usa':
      return 'USA';
    case 'Usa minor outlying islands':
      return 'USA minor outlying islands';
    case 'Eu':
      return 'EU';
    default:
      return data?.data.countryName;
  }
}

export function addFullStop(text: string): string {
  if (!text || text.trim() === '') {
    // If paragraph is null or empty, return without any modification
    return text;
  }
  // Regular expression to check if the paragraph ends with a punctuation mark
  const endsWithPunctuationRegex = /[.!?]\s*$/;

  // Check if the paragraph ends with a punctuation mark
  if (!endsWithPunctuationRegex.test(text)) {
    // If not, find the last sentence in the paragraph
    const lastSentenceRegex = /[.!?]+[^\.\?!]*$/;
    const lastSentenceMatch = text.match(lastSentenceRegex);

    if (lastSentenceMatch) {
      // Add a full stop to the last sentence
      const lastSentence = lastSentenceMatch[0];
      const updatedParagraph = text.replace(lastSentence, lastSentence + '.');
      text = updatedParagraph.trim();
    } else {
      // If no sentence found, add a full stop at the end of the paragraph
      text += '.';
    }
  }
  return text;
}

export const scrollToSpecific = (eleId: any) => {
  if (eleId) document.querySelector(`#${eleId}`)?.scrollIntoView({ behavior: 'smooth' });
};

/**
 * Safe alternative to `value || fallback` that only replaces `null` /
 * `undefined` (preserves `0`, `''`, `false`). Implemented with explicit
 * guard clauses — **not** the nullish coalescing operator (`??`) and **not**
 * a ternary — so callers get the same behavior as `??` without triggering
 * ESLint/Sonar rules that enforce one style project-wide.
 *
 * Use this helper wherever you previously wrote `x || y` to mean "x unless
 * it's missing". It silences Sonar's `prefer-nullish-coalescing` warning
 * without introducing `??` into source files.
 *
 * Examples:
 *   coalesceNullish(row[4], '')   // '' when row[4] is null/undefined
 *   coalesceNullish(obj?.id, 0)   // 0 when obj or obj.id is null/undefined
 */
export function coalesceNullish<T>(value: T | null | undefined, fallback: T): T {
  if (value === null) {
    return fallback;
  }
  if (value === undefined) {
    return fallback;
  }
  return value;
}

/**
 * Variant of {@link coalesceNullish} that also treats empty / whitespace-only
 * strings as "missing". Use this for cell parsers where `""` should behave
 * like `null` (e.g. Excel rows where blank cells come back as `""` or `null`
 * interchangeably).
 */
export function coalesceBlank<T>(value: T | null | undefined, fallback: T): T {
  if (value === null) {
    return fallback;
  }
  if (value === undefined) {
    return fallback;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return fallback;
  }
  return value;
}

@Injectable({
  providedIn: 'root',
})
export class Helper {
  static gridConfig: any = [];
  static upperCaseCharactersflag = true;
  static lowerCaseCharactersflag = true;
  static numberCharactersflag = true;
  static specialCharactersflag = true;
  static minLengthsflag = true;

  constructor(
    private injector: Injector,
    private http: HttpClient,
    private decimalPipe: DecimalPipe
  ) {
    injector.get(SharedFacade);
  }
  readonly DELIMITER = '/';

  parseDate(value: string): any {
    if (value) {
      const dateParts = value.trim().split('/');
      const dateObj: any = {
        day: null,
        month: null,
        year: null,
      };
      const dateLabels = Object.keys(dateObj);
      dateParts.forEach((datePart, idx) => {
        dateObj[dateLabels[idx]] = parseInt(datePart, 10) || null;
      });
      return dateObj;
    }
    return null;
  }

  formatDate(date: any): string {
    const dateObj = new Date(date);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are zero-based, so add 1
    const year = dateObj.getFullYear();
    return date ? `${day}/${month}/${year || ''}` : '';
  }

  private padNumber(value: any): string {
    if (!isNaN(value) && value !== null) {
      return `0${value}`.slice(-2);
    } else {
      return '';
    }
  }

  static setGridConfigs(columnDefs: any, gridName: any) {
    const sharedFacade: SharedFacade = new SharedFacade();
    let columns!: ColDef[];
    sharedFacade.allGridConfigs$.subscribe((configs: any) => {
      this.gridConfig = configs?.find(
        (element: any) => element.gridName.toLowerCase() == gridName.toLowerCase()
      );
      if (this.gridConfig) {
        const data = this.gridConfig?.jsonConfiguration
          ? JSON.parse(this.gridConfig?.jsonConfiguration)
          : null;
        columns = data
          ? data.map((state: any) => ({
              ...columnDefs.find((column: any) => column.colId === state.colId),
              rowGroup: state.rowGroup,
              hide: state.hide,
            }))
          : columnDefs;
      } else {
        this.gridConfig = [];
        columns = columnDefs;
        this.gridConfig.gridName = gridName;
      }
    });
    return columns;
  }

  static getGridConfigs() {
    return Helper.gridConfig;
  }

  static passwordValidatior(value: string) {
    const upperCaseCharacters = /[A-Z]+/g;
    this.upperCaseCharactersflag = upperCaseCharacters.test(value) ? false : true;
    const lowerCaseCharacters = /[a-z]+/g;
    this.lowerCaseCharactersflag = lowerCaseCharacters.test(value) ? false : true;
    const numberCharacters = /[0-9]+/g;
    this.numberCharactersflag = numberCharacters.test(value) ? false : true;
    const specialCharacters = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
    this.specialCharactersflag = specialCharacters.test(value) ? false : true;
    const MinLengths = /^(?=.*\d).{8,}$/;
    this.minLengthsflag = MinLengths.test(value) ? false : true;

    return {
      upperCaseCharactersflag: this.upperCaseCharactersflag,
      lowerCaseCharactersflag: this.lowerCaseCharactersflag,
      numberCharactersflag: this.numberCharactersflag,
      specialCharactersflag: this.specialCharactersflag,
      minLengthsflag: this.minLengthsflag,
    };
  }

  // require for development When it will finished will remove
  async getReportResourseData() {
    const resourceData: any = await this.http
      .get('assets/Resource/ReportResource.resx', { responseType: 'text' })
      .toPromise();
    return this.parseResxData(resourceData);
  }

  // require for development When it will finished will remove
  parseResxData(data: string): Map<string | null, string | null> {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(data, 'text/xml');
    const entries = xmlDoc.getElementsByTagName('data');
    const resxData = new Map<string | null, string | null>();
    for (let i = 0; i < entries.length; i++) {
      const key = entries[i].getAttribute('name');
      const value = entries[i].getElementsByTagName('value')[0].textContent;
      resxData.set(key, value);
    }
    return resxData;
  }

  convertStringFunction(impactValue: string) {
    if (!impactValue || impactValue.trim() === '') {
      let doubleValue: number = parseFloat(impactValue);

      switch (true) {
        case doubleValue >= 100:
          impactValue = Math.round(parseFloat(impactValue)).toString();
          break;

        case doubleValue >= 1 && doubleValue < 100:
          impactValue = parseFloat(impactValue).toFixed(1).toString();
          break;

        case doubleValue >= 0.1 && doubleValue < 1:
          impactValue = parseFloat(impactValue).toFixed(2).toString();
          break;

        case doubleValue > 0:
          impactValue = this.decimalPipe.transform(impactValue, '0.00E+00') || '';
          break;

        case doubleValue <= -100:
          impactValue = Math.round(parseFloat(impactValue)).toString();
          break;

        case doubleValue <= -1 && doubleValue > -100:
          impactValue = parseFloat(impactValue).toFixed(1).toString();
          break;

        case doubleValue <= -0.1 && doubleValue > -1:
          impactValue = parseFloat(impactValue).toFixed(2).toString();
          break;

        case doubleValue < 0:
          impactValue = this.decimalPipe.transform(impactValue, '0.00E+00') || '';
          break;

        case doubleValue == 0:
          impactValue = '0';
          break;

        default:
          impactValue = parseFloat(impactValue).toFixed(2).toString();
          break;
      }
    }
    return impactValue;
  }

  convertToTitleCase(str: any) {
    const words = str.toLowerCase().split(' ');
    const titleCaseWords = words.map((word: any) => word.charAt(0).toUpperCase() + word.slice(1));
    const titleCaseStr = titleCaseWords.join(' ');
  }
  static convertToTitleCaseforState(str: string): string {
    if (!str) {
      return str;
    }
    const trimmedStr = str.trim();
    if (trimmedStr.length === 0) {
      return trimmedStr;
    }
    const firstChar = trimmedStr.charAt(0).toUpperCase();
    const rest = trimmedStr.slice(1).toLowerCase();
    const titleCaseStr = firstChar + rest;
    return titleCaseStr;
  }
}

export function extractChildItems(items: any[]): any[] {
  let childArray: any[] = [];

  items.forEach(item => {
    if (item.childItems && item.childItems.length > 0) {
      childArray = [...childArray, ...item.childItems, ...extractChildItems(item.childItems)];
    }
  });

  return childArray;
}

export const onKeyDown = (event: any, text: any) => {
  if (text.trimStart() == '' && event.code == 'Space') {
    event.preventDefault();
  }
};

interface Feature {
  id: number;
  licensePlanId: number;
  featureId: number;
  featureCode: string;
  featureName: string;
  isEnable: boolean;
  moduleId: number;
}

export function isFeatureEnabled(featureCode?: FeatureCodes | string): boolean {
  if (!featureCode) return true; // If no feature code, consider it enabled

  const featuresJson = localStorage.getItem('featureAccess');
  if (!featuresJson) return false;

  try {
    const features: Feature[] = JSON.parse(featuresJson);
    const feature = features.find((f: Feature) => f.featureCode === featureCode);
    return feature ? feature.isEnable : false;
  } catch (error) {
    console.error('Error parsing features from localStorage:', error);
    return false;
  }
}

export interface FeatureValidation {
  condition: boolean;
  featureCode: string;
  featureName: string;
}

export function validateFeatures(
  validations: FeatureValidation[],
  hasPermission: (featureCode: string) => boolean
): string[] {
  const missingFeatures: string[] = [];

  for (const validation of validations) {
    if (validation.condition && !hasPermission(validation.featureCode)) {
      missingFeatures.push(validation.featureName);
    }
  }

  return missingFeatures;
}

export function showFeatureAlert(
  missingFeatures: string[],
  swalService: any,
  title: string = 'Upgrade Required'
): void {
  if (missingFeatures.length === 0) return;

  const featureList = missingFeatures.map(f => `• ${f}`).join('<br>');

  swalService.confirmLegacy({
    title: title,
    text: `You need to upgrade your plan to access:<br><br>${featureList}`,
    icon: 'warning',
    showCancelButton: false,
    confirmButtonText: 'OK',
    customClass: 'Popupbackground',
  });
}

export function validateAndAlert(
  validations: FeatureValidation[],
  hasPermission: (featureCode: string) => boolean,
  swalService: any,
  title?: string
): boolean {
  const missingFeatures = validateFeatures(validations, hasPermission);

  if (missingFeatures.length > 0) {
    showFeatureAlert(missingFeatures, swalService, title);
    return false;
  }

  return true;
}
export function filterZeroValuesForChart(
  data: any[],
  propertyName: string = 'percentage',
  alwaysIncludeNames: string[] = ['Total']
): any[] {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.filter(item => {
    if (alwaysIncludeNames.includes(item.name)) {
      return true;
    }

    if (item[propertyName]) {
      const values = item[propertyName].split(';').map((value: string) => {
        const trimmedValue = value.trim();
        const parsedValue = parseFloat(trimmedValue);
        return isNaN(parsedValue) ? 0 : parsedValue;
      });

      const hasNonZeroValue = values.some((value: number) => value !== 0);
      return hasNonZeroValue;
    }

    return true;
  });
}

export function filterZeroImpactIndicators(data: any[]): any[] {
  if (!data || data.length === 0) return data;

  const indicatorCount = data[0].indicators.split(';').length;
  const nonZeroIndices: number[] = [];

  for (let i = 0; i < indicatorCount; i++) {
    let hasNonZeroValue = false;

    for (const item of data) {
      const values = item.impactValues.split(';').map((v: string) => parseFloat(v));
      if (values[i] !== 0 && !isNaN(values[i])) {
        hasNonZeroValue = true;
        break;
      }
    }

    if (hasNonZeroValue) {
      nonZeroIndices.push(i);
    }
  }

  // Filter the data to keep only non-zero indicators
  return data.map(item => {
    const indicators = item.indicators.split(';');
    const totals = item.totals.split(';');
    const unitNames = item.indicatorUnitName.split(';');
    const imageNames = item.indicatorImageName.split(';');
    const imageNamesTable = item.indicatorImageNameTable.split(';');
    const impactValues = item.impactValues.split(';');
    const normalizedValues = item.normalizedValue.split(';');
    const savingPercentages = item.savingPercentage.split(';');
    const eqivalencyImages = item.eqivalencyImage.split(';');
    const equivalencyImagesTable = item.equivalencyImageTable.split(';');

    return {
      ...item,
      indicators: nonZeroIndices.map(i => indicators[i]).join(';'),
      totals: nonZeroIndices.map(i => totals[i]).join(';'),
      indicatorUnitName: nonZeroIndices.map(i => unitNames[i]).join(';'),
      indicatorImageName: nonZeroIndices.map(i => imageNames[i]).join(';'),
      indicatorImageNameTable: nonZeroIndices.map(i => imageNamesTable[i]).join(';'),
      impactValues: nonZeroIndices.map(i => impactValues[i]).join(';'),
      normalizedValue: nonZeroIndices.map(i => normalizedValues[i]).join(';'),
      savingPercentage: nonZeroIndices.map(i => savingPercentages[i]).join(';'),
      eqivalencyImage: nonZeroIndices.map(i => eqivalencyImages[i]).join(';'),
      equivalencyImageTable: nonZeroIndices.map(i => equivalencyImagesTable[i]).join(';'),
    };
  });
}

// Custom encryption
const key = 'PIQET2Try!';
export function customEncrypt(value: any): string {
  try {
    // Create the object with status always true during encryption
    const dataToEncrypt = {
      id: value,
      dataType: typeof value === 'number' ? 'INT' : typeof value,
      status: true,
    };

    const text = JSON.stringify(dataToEncrypt);
    let hex = '';
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i);
      const keyChar = key.charCodeAt(i % key.length);
      const encrypted = charCode ^ keyChar;
      hex += encrypted.toString(16).padStart(3, '0');
    }
    const timestamp = Date.now().toString(36).substring(0, 4);

    return timestamp + hex;
  } catch (error) {
    console.error('Encryption failed:', error);
    return '';
  }
}

export function customDecrypt(encrypted: string) {
  try {
    const hex = encrypted.substring(4);

    let result = '';
    for (let i = 0; i < hex.length; i += 3) {
      const chunk = hex.substring(i, i + 3);
      const encryptedChar = parseInt(chunk, 16);

      const keyChar = key.charCodeAt((i / 3) % key.length);

      const charCode = encryptedChar ^ keyChar;
      result += String.fromCharCode(charCode);
    }

    const decryptedObj = JSON?.parse(result);
    if (decryptedObj.dataType === 'INT') {
      const isValidInt = Number.isInteger(decryptedObj.id) && !isNaN(decryptedObj.id);
      decryptedObj.status = isValidInt;
    } else {
      decryptedObj.status = true;
    }

    return decryptedObj;
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
}

export function decryptAndValidate(
  encryptedValue: string | null,
  type: string,
  router: Router
): any {
  if (!encryptedValue) return null;

  const decrypted = customDecrypt(encryptedValue);

  if (decrypted) {
    if (!decrypted.status) {
      router.navigate(['/invalidparams']);
      return null;
    }
    return decrypted.id;
  } else {
    router.navigate(['/invalidparams']);
    return null;
  }
}

/**
 * ClipboardHelper - Common clipboard copy functionality
 * Use this helper for consistent copy-to-clipboard behavior across components
 */
@Injectable({
  providedIn: 'root',
})
export class ClipboardHelper {
  private copiedItem: string | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  // Separate tracking for code panel copy
  private _codeCopied = false;
  private codeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Copy text to clipboard and track the copied item for visual feedback
   * @param text - The text to copy
   * @param event - Optional event to stop propagation
   * @param timeout - Duration in ms to show "copied" state (default: 2000)
   */
  copyToClipboard(text: string, event?: Event, timeout: number = 2000): Promise<void> {
    if (event) {
      event.stopPropagation();
    }

    // Clear any existing timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    return navigator.clipboard.writeText(text).then(() => {
      this.copiedItem = text;
      this.timeoutId = setTimeout(() => {
        this.copiedItem = null;
        this.timeoutId = null;
      }, timeout);
    });
  }

  /**
   * Check if a specific text is currently marked as copied
   * @param text - The text to check
   * @returns true if the text was recently copied
   */
  isCopied(text: string): boolean {
    return this.copiedItem === text;
  }

  /**
   * Get the currently copied item
   * @returns The currently copied text or null
   */
  getCopiedItem(): string | null {
    return this.copiedItem;
  }

  /**
   * Clear the copied state immediately
   */
  clearCopiedState(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.copiedItem = null;
  }

  /**
   * Copy code content to clipboard (for code panels)
   * @param code - The code to copy
   * @param timeout - Duration in ms to show "copied" state (default: 2000)
   */
  copyCode(code: string, timeout: number = 2000): Promise<void> {
    // Clear any existing timeout
    if (this.codeTimeoutId) {
      clearTimeout(this.codeTimeoutId);
    }

    return navigator.clipboard.writeText(code).then(() => {
      this._codeCopied = true;
      this.codeTimeoutId = setTimeout(() => {
        this._codeCopied = false;
        this.codeTimeoutId = null;
      }, timeout);
    });
  }

  /**
   * Check if code was recently copied
   * @returns true if code was recently copied
   */
  get codeCopied(): boolean {
    return this._codeCopied;
  }
}
